<template>
  <v-container fluid>
    <v-overlay :model-value="loadingExport" class="align-center justify-center">
      <div class="d-flex flex-column align-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="text-h6 mt-4">Export PDF en cours...</div>
      </div>
    </v-overlay>

    <v-card>
      <v-card-title class="text-h5 pa-4">
        <v-btn
          :icon="mdiArrowLeft"
          variant="text"
          @click="modifyProjection"
          class="mr-2"
        ></v-btn>
        {{ $t("projections.resultsTitle") }}
      </v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          closable
          class="mb-4"
          @click:close="clearError"
        >
          {{ error }}
        </v-alert>

        <!-- Informations client -->
        <v-card variant="outlined" class="mb-4">
          <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
            {{ $t("projections.customerInfo") }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.customerName") }}
                </div>
                <div class="text-h6">{{ results?.customer?.name }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.customerNumber") }}
                </div>
                <div class="text-h6">{{ results?.customer?.number }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.currentStatusNumber") }}
                </div>
                <v-chip
                  :color="getStatusColor(results?.currentStatus)"
                  class="mt-1"
                >
                  {{ getStatusLabel(results?.currentStatus) }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Comparaison des résultats -->
        <v-row>
          <!-- Statistiques actuelles (N-1) -->
          <v-col cols="12" md="6">
            <v-card variant="outlined" class="h-100">
              <v-card-title class="bg-grey-lighten-4">
                <v-icon :icon="mdiChartBar" class="mr-2"></v-icon>
                {{ $t("projections.currentStats") }}
                <v-chip size="small" class="ml-2">{{ currentYear }}</v-chip>
              </v-card-title>
              <v-card-text>
                <!-- Total -->
                <v-list>
                  <v-list-item
                    class="mb-2 rounded"
                    style="background-color: #fff5e6"
                  >
                    <v-list-item-title class="text-h6">
                      {{ $t("projections.totalSales") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-h5 mt-2">
                      {{ formatCurrency(baseStats?.currentYearSales) }}
                    </v-list-item-subtitle>
                    <template v-slot:append>
                      <v-chip color="#ffb500">
                        <span style="color: black; font-weight: 600">
                          {{ formatNumber(baseStats?.currentTotalPoints) }} pts
                        </span>
                      </v-chip>
                    </template>
                  </v-list-item>

                  <v-divider class="my-3"></v-divider>

                  <!-- Détails par catégorie -->
                  <v-list-item>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-body-2">{{
                          $t("projections.capitalSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-subtitle-2 font-weight-medium">
                          {{ formatCurrency(baseStats?.projectedCapitalSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(baseStats?.currentCapitalPoints) }}
                          pts
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-body-2">{{
                          $t("projections.implantSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-subtitle-2 font-weight-medium">
                          {{ formatCurrency(baseStats?.projectedImplantSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(baseStats?.currentImplantPoints) }}
                          pts
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-body-2">{{
                          $t("projections.serviceSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-subtitle-2 font-weight-medium">
                          {{ formatCurrency(baseStats?.projectedServiceSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(baseStats?.currentServicePoints) }}
                          pts
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-body-2">{{
                          $t("projections.consumableSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-subtitle-2 font-weight-medium">
                          {{
                            formatCurrency(baseStats?.projectedConsommableSales)
                          }}
                        </span>
                        <v-chip size="small">
                          {{
                            formatNumber(baseStats?.currentConsommablePoints)
                          }}
                          pts
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Projection (N) -->
          <v-col cols="12" md="6">
            <v-card
              variant="outlined"
              class="h-100"
              style="border: 2px solid #ffb500 !important"
            >
              <v-card-title style="background-color: #ffb500; color: white">
                <v-icon
                  :icon="mdiChartLineVariant"
                  class="mr-2"
                  color="white"
                ></v-icon>
                {{ $t("projections.projectedStats") }}
                <v-chip
                  size="small"
                  class="ml-2"
                  color="white"
                  style="color: #ffb500"
                >
                  {{ results?.projectionYear }}
                </v-chip>
              </v-card-title>
              <v-card-text>
                <!-- Total -->
                <v-list>
                  <v-list-item
                    class="mb-2 rounded"
                    style="background-color: #e8f5e9"
                  >
                    <v-list-item-title class="text-h6">
                      {{ $t("projections.totalSales") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-h5 mt-2">
                      {{ formatCurrency(results?.projectedYearSales) }}
                      <v-chip
                        :color="getEvolutionColor(salesEvolution)"
                        size="small"
                        class="ml-2"
                      >
                        <v-icon
                          :icon="
                            salesEvolution >= 0
                              ? mdiTrendingUp
                              : mdiTrendingDown
                          "
                          size="small"
                          class="mr-1"
                        ></v-icon>
                        {{ formatPercentage(salesEvolution) }}
                      </v-chip>
                    </v-list-item-subtitle>
                    <template v-slot:append>
                      <v-chip color="#f89200">
                        <span style="color: black; font-weight: 600">
                          {{ formatNumber(results?.projectedTotalPoints) }} pts
                        </span>
                        <v-chip
                          :color="getEvolutionColor(pointsEvolution)"
                          size="x-small"
                          class="ml-1"
                        >
                          {{ formatPercentage(pointsEvolution) }}
                        </v-chip>
                      </v-chip>
                    </template>
                  </v-list-item>

                  <v-divider class="my-3"></v-divider>

                  <!-- Détails par catégorie -->
                  <v-list-item>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-body-2">{{
                          $t("projections.capitalSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-subtitle-2 font-weight-medium">
                          {{ formatCurrency(results?.projectedCapitalSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(results?.projectedCapitalPoints) }}
                          pts
                          <span
                            :class="
                              getEvolutionClass(
                                getEvolution(
                                  baseStats?.currentCapitalPoints,
                                  results?.projectedCapitalPoints,
                                ),
                              )
                            "
                          >
                            ({{
                              formatPercentage(
                                getEvolution(
                                  baseStats?.currentCapitalPoints,
                                  results?.projectedCapitalPoints,
                                ),
                              )
                            }})
                          </span>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-body-2">{{
                          $t("projections.implantSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-subtitle-2 font-weight-medium">
                          {{ formatCurrency(results?.projectedImplantSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(results?.projectedImplantPoints) }}
                          pts
                          <span
                            :class="
                              getEvolutionClass(
                                getEvolution(
                                  baseStats?.currentImplantPoints,
                                  results?.projectedImplantPoints,
                                ),
                              )
                            "
                          >
                            ({{
                              formatPercentage(
                                getEvolution(
                                  baseStats?.currentImplantPoints,
                                  results?.projectedImplantPoints,
                                ),
                              )
                            }})
                          </span>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-body-2">{{
                          $t("projections.serviceSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-subtitle-2 font-weight-medium">
                          {{ formatCurrency(results?.projectedServiceSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(results?.projectedServicePoints) }}
                          pts
                          <span
                            :class="
                              getEvolutionClass(
                                getEvolution(
                                  baseStats?.currentServicePoints,
                                  results?.projectedServicePoints,
                                ),
                              )
                            "
                          >
                            ({{
                              formatPercentage(
                                getEvolution(
                                  baseStats?.currentServicePoints,
                                  results?.projectedServicePoints,
                                ),
                              )
                            }})
                          </span>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-body-2">{{
                          $t("projections.consumableSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-subtitle-2 font-weight-medium">
                          {{
                            formatCurrency(results?.projectedConsommableSales)
                          }}
                        </span>
                        <v-chip size="small">
                          {{
                            formatNumber(results?.projectedConsommablePoints)
                          }}
                          pts
                          <span
                            :class="
                              getEvolutionClass(
                                getEvolution(
                                  baseStats?.currentConsommablePoints,
                                  results?.projectedConsommablePoints,
                                ),
                              )
                            "
                          >
                            ({{
                              formatPercentage(
                                getEvolution(
                                  baseStats?.currentConsommablePoints,
                                  results?.projectedConsommablePoints,
                                ),
                              )
                            }})
                          </span>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-divider class="my-3"></v-divider>

                  <!-- Statut projeté -->
                  <v-list-item>
                    <v-list-item-title>{{
                      $t("projections.projectedStatus")
                    }}</v-list-item-title>
                    <template v-slot:append>
                      <v-chip
                        :color="
                          getStatusColor(
                            results?.statusOverrideUsed
                              ? results?.statusOverrideUsed
                              : results?.projectedStatus,
                          )
                        "
                        size="small"
                      >
                        {{
                          getStatusLabel(
                            results?.statusOverrideUsed
                              ? results?.statusOverrideUsed
                              : results?.projectedStatus,
                          )
                        }}
                      </v-chip>
                    </template>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn variant="outlined" @click="goBack" :prepend-icon="mdiArrowLeft">
          {{ $t("projections.backToList") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="#535756"
          variant="outlined"
          @click="modifyProjection"
          :prepend-icon="mdiPencil"
        >
          {{ $t("projections.modify") }}
        </v-btn>
        <v-btn
          color="#f89200"
          variant="elevated"
          :prepend-icon="mdiFileExport"
          @click="exportToPdf"
          :loading="loading"
          class="text-white"
        >
          {{ $t("projections.export") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import {
  mdiArrowLeft,
  mdiChartBar,
  mdiChartLineVariant,
  mdiTrendingUp,
  mdiTrendingDown,
  mdiPencil,
  mdiFileExport,
} from "@mdi/js";
import { getStatusColor } from "@/utils/statusColors";

export default {
  name: "ProjectionResults",
  data() {
    return {
      mdiArrowLeft,
      mdiChartBar,
      mdiChartLineVariant,
      mdiTrendingUp,
      mdiTrendingDown,
      mdiPencil,
      mdiFileExport,
    };
  },
  computed: {
    ...mapState("projections", [
      "projectionResults",
      "projectionForm",
      "error",
      "currentYear",
      "currentYearStats",
      "loading",
      "loadingExport",
    ]),
    results() {
      return this.projectionResults;
    },
    // Stats N-1 : toujours depuis currentYearStats (calculées avec statut par défaut)
    baseStats() {
      return this.currentYearStats || this.projectionResults;
    },
    salesEvolution() {
      return this.getEvolution(
        this.baseStats?.currentYearSales,
        this.results?.projectedYearSales,
      );
    },
    pointsEvolution() {
      return this.getEvolution(
        this.baseStats?.currentTotalPoints,
        this.results?.projectedTotalPoints,
      );
    },
  },
  methods: {
    goBack() {
      this.$emit("go-back");
    },
    modifyProjection() {
      this.$emit("modify-projection");
    },
    clearError() {
      this.$store.commit("projections/setError", null);
    },
    async exportToPdf() {
      try {
        const exportData = {
          baseStatistics: this.baseStats,
          scenarios: [
            {
              scenarioName: `Projection ${this.results?.projectionYear}`,
              projection: this.results,
            },
          ],
        };

        const response = await this.$store.dispatch(
          "projections/exportProjectionsToPdf",
          exportData,
        );

        // Créer un blob et télécharger le PDF
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download =
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") ||
          `Projections_${this.results?.customer?.name}_${this.results?.projectionYear}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch {
        this.$store.dispatch("showSnackbar", {
          message: this.$t("alert.error.generic"),
          color: "error",
        });
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(value || 0);
    },
    formatNumber(value) {
      return new Intl.NumberFormat("fr-FR").format(Math.round(value || 0));
    },
    formatPercentage(value) {
      if (!value && value !== 0) return "N/A";
      const sign = value >= 0 ? "+" : "";
      return `${sign}${value.toFixed(1)}%`;
    },
    /**
     * Calcule l'évolution en pourcentage entre deux valeurs
     * Gère correctement les cas où la valeur de départ est négative
     */
    getEvolution(current, projected) {
      if (!current || current === 0) return 0;

      // Cas spécial : si on part d'une valeur négative
      if (current < 0) {
        // Si on passe de négatif à positif ou zéro : croissance positive
        if (projected >= 0) {
          // Calcul basé sur la valeur absolue pour avoir un pourcentage positif
          return (Math.abs(projected - current) / Math.abs(current)) * 100;
        }
        // Si on reste négatif : on inverse le calcul
        // Si projected > current (ex: -50 > -159), c'est une amélioration (positif)
        // Si projected < current (ex: -200 < -159), c'est une dégradation (négatif)
        return ((projected - current) / Math.abs(current)) * 100;
      }

      // Cas normal : valeur de départ positive
      return ((projected - current) / current) * 100;
    },
    getEvolutionColor(evolution) {
      if (evolution > 0) return "success";
      if (evolution < 0) return "error";
      return "grey";
    },
    getEvolutionClass(evolution) {
      if (evolution > 0) return "text-success";
      if (evolution < 0) return "text-error";
      return "text-grey";
    },
    getStatusColor,
    getStatusLabel(status) {
      if (status === -1 || status === 0 || status === "-1" || status === "0") {
        return "Hors programme";
      }
      return status !== undefined && status !== null ? status.toString() : "0";
    },
  },
};
</script>

<style scoped>
.v-chip {
  font-weight: 500;
}

.text-success {
  color: #4caf50 !important;
  font-weight: 600;
}

.text-error {
  color: #f44336 !important;
  font-weight: 600;
}

.text-grey {
  color: #9e9e9e !important;
}

.h-100 {
  height: 100%;
}
</style>
